// src/pages/TabWrapper.jsx
import React, { useEffect, useRef, useCallback } from "react";
import { fallbackMenuIcon, normalizeBootstrapIconClass } from "../utils/sidebarUtils";
import Table from "../components/Table";
import { tableContent } from "../utils/TableData";
import PendingApproval from "./admin/dashboard/pending-approval";
import Modules from "./admin/module-management/modules";
import Users from "./admin/user-management/users";
import Tenants from "./admin/dashboard/tenants";
import RoleManagement from "./tenant/role-management/RoleManagement";
import RoleManagementAdmin from "./tenant/role-management-admin/RoleManagementAdmin";
import FacilityManagement from "./tenant/facility-management/FacilityManagement";
import UserRoleManagement from "./tenant/user-role-management/UserRoleManagement";
import PartnerManagement from "./tenant/partner-management/PartnerManagement";
import CustomerManagement from "./tenant/customer-management/CustomerManagement";
import SupplierManagement from "./tenant/supplier-management/SupplierManagement";
import ASNManagement from "./asn/ASNManagement";
import PoDetails from "./poDetails/PODetails";
import SalesOrder from "./salesOrder/SalesOrder";
import GoodsReceipt from "./Grn/GoodsReceipt";
import StockTransferOutManagement from "./Grn/StockTransferOutManagement";
import StockTransferInManagement from "./Grn/StockTransferInManagement";
import GatePassManagement from "./Grn/GatePassManagement";
import Return from "./return/Return";
import Invoicing from "./Invoicing/Invoicing";
import ShippingManagement from "./shippment/ShippingManagement";
import SettingsContent from "./Settings/SettingsContent";
import SkuMaster from "./sku-management/sku-master/SkuMaster";
import InventoryLedger from "./sku-management/inventory-ledger/InventoryLedger";
import PricingFoundation from "./sku-management/pricing-foundation/PricingFoundation";
import CustomerGroup from "./sku-management/customer-group/CustomerGroup";
import InitiatePackingManagement from "./InitiatePacking/InitiatePackingManagement.jsx";
import Inventory from "./sku-management/Inventory/Inventory.jsx";
import ItemLocation from "./sku-management/item-location/ItemLocation.jsx";
import HoldInventory from "./sku-management/hold-inventory/HoldInventory.jsx";
import LotAndItem from "./sku-management/lot-and-item/LotAndItem.jsx";
import AdminSettingsContent from "./Settings/AdminSetting.jsx";
import TenantDashboard from "./tenant/dashboard/TenantDashboard.jsx";
import Tools from "./Settings/Tools.jsx";
import CycleCountImportManagement from "./cyclecount/CycleCountImportManagement.jsx";
import RequestCycleCountManagement from "./cyclecount/RequestCycleCountManagement.jsx";
import AdjustmentManagement from "./adjustment/AdjustmentManagement.jsx";
import AppointmentManagement from "./appointment/AppointmentManagement.jsx";

import ChannelMgt from "./oms/channel/ChannelMgt.jsx";
import PackagingMasterMgt from "./packing/packaging_Master/PkgMasterMgt.jsx";
import PkgManagement from "./packing/packaging_Management/PkgManagement.jsx";
import CustManagement from "./oms/customer_Management/CustManagement.jsx";


import GeoManagement from "./Geography/GeoManagement.jsx";

import OrganizationManagement from "./tenant/organization-management/OrganizationManagement.jsx";
import OrganizationHierarchyManagement from "./tenant/organization-management/OrganizationHierarchyManagement.jsx";
import CustomerOnboarding from "./tenant/customer-onboarding/CustomerOnboarding.jsx";

import LocationManagement from "./oms/location_Management/LocationManagement.jsx";
import SKUManagement from "./oms/Sku/SkuMgt.jsx";

import SKUPricingManagement from "./oms/Sku_Pricing/SkupricingMgt.jsx";

import Hsn from "./oms/hsn/hsn.jsx";
import Ratecardlist from "./oms/ratecard/RatecardList.jsx";
import Courierlist from "./oms/courier/Courierlist.jsx";
import WorkstationManagement from "./workstation/WorkstationManagement.jsx";
import InventoryManagement from './oms/inventory/inventoryMgt.jsx'
import ReturnMDManagement from "./returnMD/ReturnMDManagement.js";
import CatalogMgt from './oms/catalog/CatalogMgt.jsx';
import OrderManagement from './oms/order/OrderManagement.jsx';
import DoorlocManagement from "./doorlocation/DoorLocManagenent";
import CustomerSkuPricingList from "./sku-management/pricing-foundation/CustomerSkuPricingList.jsx";
import BrandManagement from "./tenant/brand-management/BrandManagement.jsx";

/**
 * Stable-keyed TabWrapper
 * - caches mounted components by a stable unique key per tab (not by index)
 * - removes cache entry when the tab is closed
 * - keeps UI same as original (tab-bar, tab, close-btn, tab-content)
 */

const tabIconMap = {
  poDetails: "bi bi-file-earmark-text",
  PoDetails: "bi bi-file-earmark-text",
  ASNManagement: "bi bi-truck",
  SalesOrder: "bi bi-cart-check",
  Shipment: "bi bi-box-seam",
  GoodsReceipt: "bi bi-clipboard-check",
  StockTransferOut: "bi bi-box-arrow-up",
  "Stock Transfer Out": "bi bi-box-arrow-up",
  StockTransferIn: "bi bi-box-arrow-in-down",
  "Stock Transfer In": "bi bi-box-arrow-in-down",
  GatePass: "bi bi-door-open",
  "Gate Pass": "bi bi-door-open",
  Dashboard: "bi bi-speedometer2",
  RoleManagement: "bi bi-person-badge",
  RoleManagementAdmin: "bi bi-person-badge",
  FacilityManagement: "bi bi-building",
  UserRoleManagement: "bi bi-people",
  PartnerManagement: "bi bi-handshake",
  CustomerManagement: "bi bi-person-vcard",
  SupplierManagement: "bi bi-shop",
  OrganizationManagement: "bi bi-building-gear",
  OrganizationHierarchy: "bi bi-diagram-2",
  CustomerOnboarding: "bi bi-person-plus",
  SkuMaster: "bi bi-box",
  InventoryLedger: "bi bi-journal-text",
  PricingFoundation: "bi bi-tags",
  CustomerGroup: "bi bi-people",
  Inventory: "bi bi-archive",
  ItemLocation: "bi bi-geo-alt",
  HoldInventory: "bi bi-pause-circle",
  LotAndItem: "bi bi-layers",
  Returns: "bi bi-arrow-return-left",
  Return: "bi bi-arrow-return-left",
  Invoicing: "bi bi-receipt",
  Settings: "bi bi-gear",
  "Initiate Packing": "bi bi-boxes",
  Adjustment: "bi bi-sliders",
  Appointment: "bi bi-calendar-check",
  Import_Cycle_Count: "bi bi-arrow-repeat",
  Request_Cycle_Count: "bi bi-arrow-repeat",
  Tenants: "bi bi-buildings",
  PendingApproval: "bi bi-hourglass-split",
  Modules: "bi bi-grid",
  Users: "bi bi-person",
  OrderMgt: "bi bi-bag",

  AdminSettings: "bi bi-gear-wide-connected",
  ReturnMgt: "bi bi-arrow-left-right",
  GeographyMgt: "bi bi-globe2",
  workStationMgt: "bi bi-pc-display",
  AdjustmentManagement: "bi bi-sliders",
  AppointmentManagement: "bi bi-calendar-check",
  DoorLocManagenent: "bi bi-door-closed",
  Tools: "bi bi-tools",
  ChannelMgt: "bi bi-diagram-3",
  PkgMaster: "bi bi-box-seam",
  PkgManagement: "bi bi-boxes",
  custMgt: "bi bi-people",
  LocationMgt: "bi bi-geo-alt",
  Location: "bi bi-geo-alt",
  SkuMgt: "bi bi-box-seam",
  ItemPricing: "bi bi-tags",
  hsnMgt: "bi bi-receipt",
  rateMaster: "bi bi-currency-exchange",
  courierMgt: "bi bi-truck",
  InventoryMgt: "bi bi-clipboard-data",
  CatalogueMgt: "bi bi-collection",
  /** Legacy / sample sidebar keys */
  pendingApprovals: "bi bi-hourglass-split",
  TenantRoles: "bi bi-person-badge",
};

const componentMap = {
  Tenants,
  PendingApproval,
  Modules,
  Users,
  RoleManagement,
  RoleManagementAdmin,
  FacilityManagement,
  UserRoleManagement,
  PartnerManagement,
  CustomerManagement,
  SupplierManagement,
  OrganizationManagement,
  OrganizationHierarchy: OrganizationHierarchyManagement,
  CustomerOnboarding,
  ASNManagement,
  poDetails: PoDetails,
  PoDetails,
  SalesOrder,
  GoodsReceipt,
  StockTransferOut: StockTransferOutManagement,
  StockTransferIn: StockTransferInManagement,
  "Stock Transfer Out": StockTransferOutManagement,
  "Stock Transfer In": StockTransferInManagement,
  GatePass: GatePassManagement,
  "gatepass": GatePassManagement,
  Inventory,
  ItemLocation,
  HoldInventory,
  LotAndItem,
  Returns: Return,
  Return,
  Invoicing,
  Shipment: ShippingManagement,
  Settings: SettingsContent,
  SkuMaster,
  InventoryLedger,
  "Pricing module for customer ":PricingFoundation,
  CustomerGroup,
  "Initiate Packing": InitiatePackingManagement,
  AdminSettings: AdminSettingsContent,
   Dashboard: TenantDashboard,
   item_master:CustomerSkuPricingList,

  ReturnMgt: ReturnMDManagement,
  GeographyMgt: GeoManagement,
  workStationMgt: WorkstationManagement,

  // CycleCount: CycleCountImportManagement,
  Import_Cycle_Count: CycleCountImportManagement,
  Request_Cycle_Count: RequestCycleCountManagement,
  AdjustmentManagement: AdjustmentManagement,
  AppointmentManagement: AppointmentManagement,
  /** Alias: ASN list / cycle count open `/appointment` with content "Appointment" */
  Appointment: AppointmentManagement,
  DoorLocManagenent: DoorlocManagement,
  Tools: Tools,
  ChannelMgt: ChannelMgt,
  PkgMaster: PackagingMasterMgt,
  PkgManagement: PkgManagement,
  custMgt: CustManagement,
  LocationMgt: LocationManagement,
  SkuMgt: SKUManagement,
  ItemPricing: SKUPricingManagement,
  hsnMgt: Hsn,
  rateMaster: Ratecardlist,
  courierMgt: Courierlist,
  InventoryMgt: InventoryManagement,
  CatalogueMgt: CatalogMgt,
  OrderMgt: OrderManagement,
  "stock-transfer-out": StockTransferOutManagement,
  "stock-transfer-in": StockTransferInManagement,
  "gate-pass": GatePassManagement,
  BrandManagement: BrandManagement,
};

/**
 * Menus come from the API; if a "Customer Management" / "Supplier Management" leaf
 * arrives with a content key we don't know, resolve the screen from its title so
 * the tab never falls back to the welcome placeholder.
 */
const resolveComponentByTitle = (title) => {
  const t = String(title || "").toLowerCase().replace(/[_-]+/g, " ");
  if (t.includes("item") && t.includes("location")) return ItemLocation;
  if (t.includes("hold") && t.includes("inventory")) return HoldInventory;
  if (t.includes("lot") && t.includes("item") && !t.includes("location")) return LotAndItem;
  if (!t.includes("management")) return null;
  if (t.includes("customer")) return CustomerManagement;
  if (t.includes("supplier")) return SupplierManagement;
  return null;
};

const WelcomeFallback = () => (
  <div className="welcome-portal">
    <h2>Welcome to the Partner Portal</h2>
    <p>Manage your partners, users, roles, and facilities all in one place.</p>
  </div>
);

const TabWrapper = ({ tabs = [], activeTab = 0, setActiveTab = () => { }, closeTab = () => { } }) => {
  // refs
  const tabRefs = useRef([]);
  // cache keyed by stable uniqueKey (not index)
  const renderedTabComponents = useRef({});

  // helper to compute a stable unique key for each tab
  const getUniqueKey = useCallback((tab, idx) => {
    if (!tab) return `tab_${idx}`;
    return tab.key ?? tab.path ?? tab.id ?? `${tab.title ?? "tab"}_${idx}`;
  }, []);

  // create + cache component for a given tab (by uniqueKey)
  const createAndCacheElement = useCallback((tab, idx) => {
    const uniq = getUniqueKey(tab, idx);
    if (renderedTabComponents.current[uniq]) return renderedTabComponents.current[uniq];

    const contentKey = tab?.content;
    const Comp =
      resolveComponentByTitle(tab?.title) ||
      componentMap[contentKey];
    let element;

    if (Comp) {
      element = (
        <div key={uniq} data-tab-uniq={uniq}>
          {React.createElement(Comp, tab?.componentProps || {})}
        </div>
      );
    } else if (tableContent[contentKey]) {
      element = (
        <div key={uniq} data-tab-uniq={uniq}>
          <Table
            rowData={tableContent[contentKey]?.rowData}
            columnDefs={tableContent[contentKey]?.columnDefs}
          />
        </div>
      );
    } else {
      element = (
        <div key={uniq} data-tab-uniq={uniq}>
          <WelcomeFallback />
        </div>
      );
    }

    renderedTabComponents.current[uniq] = element;
    return element;
  }, [getUniqueKey]);

  // scroll active tab into view
  useEffect(() => {
    const el = tabRefs.current[activeTab];
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }
  }, [activeTab, tabs.length]);

  // wrapper for closing a tab:
  // 1) remove cached element for that tab's uniqueKey
  // 2) call parent closeTab(idx)
  const handleCloseTab = useCallback((idx) => {
    const tab = tabs[idx];
    const uniq = getUniqueKey(tab, idx);
    // remove cache entry
    if (uniq && renderedTabComponents.current[uniq]) {
      try { delete renderedTabComponents.current[uniq]; } catch (e) { renderedTabComponents.current[uniq] = undefined; }
    }
    // call parent to actually remove tab from list
    closeTab(idx);
  }, [tabs, closeTab, getUniqueKey]);

  return (
    <div className="tab-wrapper">
      <div className="tab-bar">
        {tabs.map((tab, idx) => {
          const tabIcon =
            normalizeBootstrapIconClass(tab.iconClass ?? tab.icon) ||
            tabIconMap[tab.content] ||
            fallbackMenuIcon(tab);
          return (
          <div
            key={getUniqueKey(tab, idx)}
            ref={el => (tabRefs.current[idx] = el)}
            className={`tab${activeTab === idx ? " active" : ""}`}
            onClick={() => setActiveTab(idx)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setActiveTab(idx); } }}
          >
            {tabIcon && (
              <span className="tab-icon-badge">
                <i className={`${tabIcon} tab-icon`}></i>
              </span>
            )}
            <span className="tab-title">{tab.title}</span>
            {tabs.length > 1 && (
              <span
                className="close-btn"
                onClick={e => { e.stopPropagation(); handleCloseTab(idx); }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); handleCloseTab(idx); } }}
              >
                ×
              </span>
            )}
          </div>
        );
        })}
      </div>

      <div className="tab-content">
        {tabs.map((tab, idx) => {
          const uniq = getUniqueKey(tab, idx);
          const element = createAndCacheElement(tab, idx);
          const isActive = activeTab === idx;
          return (
            <div
              key={`tab-container-${uniq}`}
              className={`tab-pane-view${isActive ? " is-active" : ""}`}
              aria-hidden={!isActive}
            >
              {element}
            </div>
          );
        })}

        {(!tabs || tabs.length === 0) && (
          <div className="welcome-portal">
            <h2>Welcome to the Partner Portal</h2>
            <p>Manage your partners, users, roles, and facilities all in one place.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TabWrapper;
